import { type GoogleProtobufTimestamp, PayoutTimeGranularity } from "../../gql-generated/types";
import { useLensPayoutHistory } from "../../lens-creator-payout/hooks/useLensPayoutHistory";
import { usePayoutStatsHistory } from "../../lens-insights/hooks/usePayoutStatsHistory";
import { timestampToDate } from "../../shared/utils/date/timestampToDate";
import { lensDetailsPayoutHistory } from "../api/queries/lensDetailsPayoutHistory";
import { type LensPlusEarnings } from "../models";
import { buildLensPlusEarnings, toUtcMonthStart } from "../utils";

import { useLensDetailsScores } from "./useLensDetailsScores";

const HISTORY_LIMIT = 50;

interface UseLensPlusEarningsParams {
    lensId: string;
    unlockableId: string;
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null;
}

interface LensPlusEarningsResult extends LensPlusEarnings {
    loading: boolean;
    error?: Error;
    // Only the month tiles depend on lensScores; history and total still render without it.
    scoresError?: Error;
}

export const useLensPlusEarnings = ({
    lensId,
    unlockableId,
    markedAsExclusiveAt,
}: UseLensPlusEarningsParams): LensPlusEarningsResult => {
    const currentMonthStart = toUtcMonthStart(new Date());
    const scores = useLensDetailsScores(unlockableId, currentMonthStart);
    const lifetime = useLensPayoutHistory({ lensId });
    const exclusiveSince = timestampToDate(markedAsExclusiveAt);
    // payoutStats is bucketed by upload month, one month after earnings. Lens+ and Rewards are mutually exclusive.
    const historyParams = {
        countryFilters: [],
        granularity: PayoutTimeGranularity.TimeGranularityMonthly,
        limit: HISTORY_LIMIT,
        unlockableId,
        startDate: exclusiveSince ? toUtcMonthStart(exclusiveSince, 1) : undefined,
    };
    const payoutBasisHistory = usePayoutStatsHistory({ ...historyParams, query: lensDetailsPayoutHistory });
    // TODO(LOOK-171944): remove once the payout-basis fields are deployed; rows show "-" for them until then.
    const fallbackHistory = usePayoutStatsHistory({ ...historyParams, skip: !payoutBasisHistory.error });
    const history = payoutBasisHistory.error ? fallbackHistory : payoutBasisHistory;

    const lensResults = scores.data?.lensScores.lensResults ?? [];

    return {
        ...buildLensPlusEarnings({
            // unlockableId is a Float in lensScores and a String on the lens.
            scores: lensResults.find((result) => String(result.unlockableId) === unlockableId),
            periods: history.periods,
            totalEarnedMilliUsd: lifetime.totalEarningsMilliUsd,
            currentMonthStart,
        }),
        // cache-and-network keeps loading true while refreshing; only block on the first response.
        loading: (scores.loading && !scores.data) || lifetime.loading || history.loading,
        error: lifetime.error ?? history.error,
        scoresError: scores.error,
    };
};
